/**
 * ClientSummaryPage — o Resumo do Cliente (`/clients/:id`).
 *
 * Substitui a antiga aba "Histórico" do diálogo de edição, que listava só
 * `quotes` e navegava para uma rota inexistente. Aqui entra TUDO que o cliente
 * já movimentou (as sete tabelas com `client_id`), com os indicadores do
 * relacionamento e uma linha do tempo unificada.
 *
 * Por que uma PÁGINA e não uma aba: é o padrão do próprio app — OS, contratos e
 * recibos todos têm rota de detalhe `/:id`; clientes era a exceção presa num
 * modal. O diálogo continua sendo o lugar de EDITAR os dados; o resumo é o lugar
 * de LER o relacionamento. A renderização do histórico existe só aqui — o
 * diálogo apenas aponta para cá.
 *
 * Continua GRÁTIS de propósito: é o usuário vendo os próprios dados, não gera
 * documento nem produz saída. Sem `useFeature`, sem paywall.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Mail, MapPin, Pencil, Phone, User, Users } from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { getCachedClients } from '@/lib/offlineDataCache';
import { useClientSummary } from '@/hooks/useClientSummary';
import { buildClientTimeline, computeClientMetrics } from '@/lib/clientSummary';
import { ClientSummaryMetrics } from '@/components/clients/ClientSummaryMetrics';
import { ClientTimelineList } from '@/components/clients/ClientTimelineList';
import { SendMessageDialog } from '@/components/messages/SendMessageDialog';
import { normalizeWhatsAppPhone } from '@/lib/messageTemplates';
import type { Client } from '@/types/database';

/** Cabeçalho com o voltar — repetido nos estados de carregando/erro. */
function Header({ onBack, title }: { onBack: () => void; title: string }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <Button variant="ghost" size="icon" onClick={onBack} aria-label="Voltar">
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <h1 className="min-w-0 flex-1 truncate text-xl font-bold">{title}</h1>
    </div>
  );
}

export default function ClientSummaryPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const [sendOpen, setSendOpen] = useState(false);

  // O cliente em si. Offline-first pelo cache que a lista de clientes mantém.
  const { data: client, isLoading: loadingClient } = useQuery({
    queryKey: ['client-record', companyId, id],
    enabled: Boolean(user?.id && companyId && id),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () =>
      user?.id && id
        ? ((getCachedClients(user.id) ?? []).find((c: any) => c?.id === id) as Client | undefined)
        : undefined,
    queryFn: async (): Promise<Client | null> => {
      if (!user?.id || !companyId || !id) return null;
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) {
        // Falha de rede não pode apagar a tela: cai no último snapshot local.
        const cached = (getCachedClients(user.id) ?? []).find((c: any) => c?.id === id);
        return (cached as Client) ?? null;
      }
      return (data as Client) ?? null;
    },
  });

  const { sources, isLoading: loadingSummary } = useClientSummary(id);

  const timeline = useMemo(() => buildClientTimeline(sources), [sources]);
  const metrics = useMemo(() => computeClientMetrics(sources), [sources]);

  if (!user) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/clients')} title="Resumo do cliente" />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Users className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <p className="mb-4 text-muted-foreground">Faça login para ver o resumo do cliente.</p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  if (!client) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/clients')} title="Resumo do cliente" />
        {loadingClient ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <User className="mb-4 h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">Cliente não encontrado.</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/clients')}>
                Voltar para clientes
              </Button>
            </CardContent>
          </Card>
        )}
      </MainLayout>
    );
  }

  const phone = normalizeWhatsAppPhone(client.phone);
  const email = client.email?.trim() || '';

  return (
    <MainLayout showFab={false}>
      <Header onBack={() => navigate('/clients')} title={client.name} />

      <div className="space-y-4">
        {/* Contato rápido — a página existe, então as ações ficam à mão. */}
        <Card>
          <CardContent className="space-y-3 p-4">
            {client.address && (
              <button
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      client.address as string,
                    )}`,
                    '_blank',
                  )
                }
                className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
              >
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{client.address}</span>
              </button>
            )}

            <div className="flex flex-wrap gap-2">
              {phone && (
                <>
                  {/* Passa pelos Modelos de Mensagens: enviar é grátis. */}
                  <Button3D
                    color="whatsapp"
                    size="sm"
                    className="flex-1"
                    onClick={() => setSendOpen(true)}
                  >
                    <WhatsAppIcon className="mr-2 h-4 w-4" />
                    WhatsApp
                  </Button3D>
                  <Button3D
                    color="gray"
                    size="sm"
                    className="flex-1"
                    onClick={() => window.open(`tel:${phone}`, '_self')}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Ligar
                  </Button3D>
                </>
              )}
              {email && (
                <Button3D
                  color="gray"
                  size="sm"
                  className="flex-1"
                  onClick={() => window.open(`mailto:${email}`, '_self')}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  E-mail
                </Button3D>
              )}
              <Button3D
                color="gray"
                size="sm"
                className="flex-1"
                onClick={() => navigate(`/clients/${client.id}/edit`)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </Button3D>
            </div>
          </CardContent>
        </Card>

        {loadingSummary && timeline.length === 0 ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <>
            <ClientSummaryMetrics metrics={metrics} />

            <div>
              <h2 className="mb-2 text-sm font-medium text-muted-foreground">
                Linha do tempo
                {metrics.totalDocuments > 0 && ` (${metrics.totalDocuments})`}
              </h2>
              <ClientTimelineList entries={timeline} />
            </div>
          </>
        )}
      </div>

      {phone && (
        <SendMessageDialog
          open={sendOpen}
          onOpenChange={setSendOpen}
          category="generic"
          context={{
            cliente: client.name,
            empresa: activeCompany?.name ?? null,
            data: new Date(),
          }}
          phone={client.phone}
          email={email || undefined}
          channels={email ? ['whatsapp', 'email'] : ['whatsapp']}
          emailSubject={activeCompany?.name ?? 'Contato'}
        />
      )}
    </MainLayout>
  );
}
